import { useEffect, useRef } from 'react'

import { pageHeaderHeight } from '@/constants'
import { useLayout } from '@/providers'
import IconButton from '@components/IconButton/IconButton'
import Logo from '@components/Logo/Logo'
import PermissionsGuard, { Permissions } from '@components/PermissionsGuard/PermissionsGuard'
import {
	faArrowLeftToDottedLine,
	faArrowRightToDottedLine,
	faBasketShopping,
	faChartMixed,
	faFile,
	faLightbulbGear,
	faServer,
	faShuffle,
	faUser,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useUpdatePreferences from '@hooks/useUpdatePreferences'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { Divider, List, Stack, useTheme } from '@mui/material'
import { Link, useLocation } from 'react-router-dom'

import NavButton from './components/NavButton'

export default function SidebarContent() {
	const { sidebarExpanded, setSidebarExpanded } = useLayout()
	const theme = useTheme()
	const { pathname } = useLocation()
	const url = removeQueryParameters(pathname)
	const isAnalyticsRoute = url.startsWith('/app/market-baskets/analytics')
	const isMarketBasketsRoute = url.startsWith('/app/market-baskets') && !isAnalyticsRoute
	const isTaskServerRoute = url.startsWith('/app/task-server')
	const switchTrackingEnabled = useBoolVariation('feature-art-1567-switch-tracking', false)
	const legacyOppyViewEnabled = useBoolVariation('fearture-art-1611-legacy-oppy-view', false)

	const { mutate: updateSidebarPref, isPending } = useUpdatePreferences()
	const isUserToggle = useRef(false)

	const handleChangeSidebarSize = (e) => {
		// prevent closing of temp sidebar on click
		e.stopPropagation()
		isUserToggle.current = true
		setSidebarExpanded((prev) => !prev)
	}

	// Persist the preference after render so the sidebar grow/shrink animation isn't blocked by the api call
	useEffect(() => {
		if (!isUserToggle.current) return
		isUserToggle.current = false
		updateSidebarPref({
			userPreferences: [{ key: 'sidebarExpanded', value: sidebarExpanded.toString() }],
		})
	}, [sidebarExpanded, updateSidebarPref])

	return (
		<>
			<Stack
				direction="row"
				alignItems="center"
				justifyContent="space-between"
				height={`calc(${pageHeaderHeight} + 2px)`} // Adding extra pixels for mui drawer weirdness
				width="100%"
				sx={{
					pl: 2,
					overflow: 'hidden',
					bgcolor: 'primary.main',
				}}
			>
				{sidebarExpanded && (
					<Link to="/app" style={{ width: '100%' }} data-testid="sidebar-logo">
						<Logo variant="white" style={{ marginTop: '4px', height: '1.75rem' }} />
					</Link>
				)}

				<IconButton
					disabled={isPending}
					onClick={handleChangeSidebarSize}
					icon={sidebarExpanded ? faArrowLeftToDottedLine : faArrowRightToDottedLine}
					sx={{ color: 'primary.contrastText' }}
					data-testid="sidebar-expand-button"
				/>
			</Stack>

			<Stack
				sx={{ width: '100%', borderRight: `1px solid ${theme.palette.divider}` }}
				height="100%"
				justifyContent="space-between"
			>
				<List sx={{ height: '100%', paddingY: '1rem' }} component="nav">
					<Stack gap={1}>
						<PermissionsGuard disableNotAuthorized showWhenHasPermission={[Permissions.BasketEdit]}>
							<NavButton
								icon={
									<FontAwesomeIcon style={{ height: '1.5rem' }} size="sm" icon={faBasketShopping} />
								}
								to="/app/market-baskets"
								selected={isMarketBasketsRoute}
								primary="Market Baskets"
								data-testid="sidebar-market-basket"
							/>
						</PermissionsGuard>

						<PermissionsGuard
							disableNotAuthorized
							showWhenHasPermission={[Permissions.BiDashboardView]}
						>
							<NavButton
								selected={isAnalyticsRoute}
								to="/app/market-baskets/analytics"
								icon={
									<FontAwesomeIcon style={{ height: '1.5rem' }} size="sm" icon={faChartMixed} />
								}
								primary="Analytics"
								data-testid="sidebar-market-basket-analytics"
							/>
						</PermissionsGuard>

						{switchTrackingEnabled && (
							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.SwitchTracking]}
							>
								<NavButton
									selected={url.startsWith('/app/switch-tracking')}
									to="/app/switch-tracking"
									icon={<FontAwesomeIcon style={{ height: '1.5rem' }} size="sm" icon={faShuffle} />}
									primary="Switch Tracking"
									data-testid="sidebar-switch-tracking"
								/>
							</PermissionsGuard>
						)}

						<PermissionsGuard
							disableNotAuthorized
							showWhenHasPermission={[Permissions.MarketBasketSettings]}
						>
							<Divider variant="middle" />

							<NavButton
								selected={url.startsWith('/app/global-basket-settings')}
								to="/app/global-basket-settings"
								icon={
									<FontAwesomeIcon style={{ height: '1.5rem' }} size="sm" icon={faLightbulbGear} />
								}
								primary="Basket Settings"
								data-testid="sidebar-global-basket-settings"
							/>
						</PermissionsGuard>

						{legacyOppyViewEnabled && (
							<PermissionsGuard
								disableNotAuthorized
								showWhenHasPermission={[Permissions.MarketBasketSettings]}
							>
								<NavButton
									selected={url.startsWith('/app/oppy-reports')}
									to="/app/oppy-reports"
									icon={<FontAwesomeIcon style={{ height: '1.25rem' }} size="sm" icon={faFile} />}
									primary="Oppy Reports"
									data-testid="sidebar-oppy-reports"
								/>
							</PermissionsGuard>
						)}

						<PermissionsGuard
							disableNotAuthorized
							showWhenHasPermission={[Permissions.TaskServerView]}
						>
							<NavButton
								selected={isTaskServerRoute}
								to="/app/task-server"
								icon={<FontAwesomeIcon style={{ height: '1.5rem' }} size="sm" icon={faServer} />}
								primary="Task Server"
								data-testid="sidebar-task-server"
							/>
						</PermissionsGuard>

						<PermissionsGuard
							disableNotAuthorized
							showWhenHasPermission={[Permissions.ManageUsers]}
						>
							<Divider variant="middle" />

							<NavButton
								to="/app/user-management"
								selected={url === '/app/user-management'}
								icon={<FontAwesomeIcon style={{ height: '1.5rem' }} size="sm" icon={faUser} />}
								primary="User Management"
								data-testid="sidebar-user-management"
							/>
						</PermissionsGuard>
					</Stack>
				</List>
			</Stack>
		</>
	)
}

function removeQueryParameters(pathname: string) {
	const urlParts = pathname.split('?')
	return urlParts[0]
}
